import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../lib/axios';
import { Coins } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { CREDITS_UPDATED_EVENT, OPEN_CREDITS_EVENT, formatCredits } from '../lib/credits';

export default function CreditBadge() {
  const [balance, setBalance] = useState(null);
  const [creditValue, setCreditValue] = useState(null);
  const [packages, setPackages] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [deltaFlash, setDeltaFlash] = useState(0);
  const flashTimer = useRef(null);

  const LIMIT = 50;

  // Fetch credits
  const fetchCredits = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/me/credits');
      setBalance(res.data.credits_balance);
      setCreditValue(res.data.credit_value_mxn);
      setPackages(res.data.packages || []);
      setLedger(res.data.ledger || []);
      setSubscription(res.data.subscription || null);
    } catch (err) {
      console.error('Error fetching credits:', err);
    }
  }, []);

  // Fetch usage history
  const fetchUsage = useCallback(async (off) => {
    setLoadingUsage(true);
    try {
      const res = await axiosInstance.get(`/me/usage?limit=${LIMIT}&offset=${off}`);
      setUsage({
        items: res.data.items || [],
        total: res.data.total || 0
      });
    } catch (err) {
      console.error('Error fetching usage:', err);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  // On mount: fetch credits once
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Listen to CREDITS_UPDATED_EVENT
  useEffect(() => {
    const handleCreditsUpdated = (e) => {
      if (typeof e.detail.balance === 'number') {
        setBalance(e.detail.balance);
      }
      if (e.detail.charged > 0) {
        setDeltaFlash(e.detail.charged);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setDeltaFlash(0), 2500);
      }
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    return () => {
      window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  // Open the modal when a blocked action fires the "buy credits" invite.
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener(OPEN_CREDITS_EVENT, openIt);
    return () => window.removeEventListener(OPEN_CREDITS_EVENT, openIt);
  }, []);

  // When modal opens, fetch usage and refresh credits
  useEffect(() => {
    if (open) {
      fetchCredits();
      fetchUsage(0);
      setOffset(0);
    }
  }, [open, fetchCredits, fetchUsage]);

  // When offset changes, fetch new usage data
  useEffect(() => {
    if (open) {
      fetchUsage(offset);
    }
  }, [offset, open, fetchUsage]);

  // Map kind to label
  const kindLabels = {
    chat: 'Chat',
    image: 'Imagen',
    describe: 'Descripción',
    improve: 'Mejora',
    test: 'Prueba'
  };

  const getKindLabel = (kind) => kindLabels[kind] || kind;

  // Concepto legible de un movimiento del ledger (compra/otorgado/ajuste/consumo).
  const reasonLabels = {
    purchase: 'Compra',
    manual_grant: 'Otorgado',
    adjust: 'Ajuste',
    usage: 'Consumo',
  };
  const getReasonLabel = (reason) => reasonLabels[reason] || reason;

  // Pagination helpers
  const canPrevious = offset > 0;
  const canNext = offset + LIMIT < usage.total;
  const displayEnd = Math.min(offset + LIMIT, usage.total);
  const displayStart = usage.total > 0 ? offset + 1 : 0;

  return (
    <>
      {/* Badge Button */}
      <button
        type="button"
        data-testid="credits-badge"
        onClick={() => setOpen(true)}
        title="Mis créditos"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all text-sm font-semibold text-slate-800 dark:text-slate-100"
      >
        <Coins className="w-4 h-4 text-amber-500" />
        <span>{balance === null ? '—' : formatCredits(balance)} créditos</span>
        {deltaFlash > 0 && (
          <span
            key={deltaFlash}
            className="text-red-600 dark:text-red-400 animate-pulse"
          >
            −{formatCredits(deltaFlash)}
          </span>
        )}
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-0 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mis créditos</DialogTitle>
            <DialogDescription>
              Consulta tu saldo, el valor del crédito, tus movimientos (créditos
              otorgados/comprados) y tu historial de consumo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Summary */}
            <div>
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                {balance === null ? '—' : formatCredits(balance)} créditos
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Valor del crédito: ${creditValue ? Number(creditValue).toFixed(2) : '—'} MXN
              </div>
              <div className="text-sm mt-1">
                Plan de plataforma:{' '}
                {subscription?.active
                  ? <span className="text-emerald-600 dark:text-emerald-400">Activo hasta {new Date(subscription.until).toLocaleDateString('es-MX')}</span>
                  : <span className="text-slate-500 dark:text-slate-400">Sin plan activo</span>}
              </div>
            </div>

            {/* Packages */}
            {packages.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                  Paquetes disponibles
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                    >
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {pkg.name}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {pkg.credits} créditos
                      </div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
                        ${Number(pkg.price_mxn).toFixed(2)} MXN
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                  Precios informativos (pre-IVA). La recarga la habilita el administrador.
                </p>
              </div>
            )}

            {/* Movimientos de créditos: compras/otorgados/ajustes + consumo. */}
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                Movimientos de créditos
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 px-2">Fecha</th>
                      <th className="py-2 px-2">Concepto</th>
                      <th className="py-2 px-2 text-right">Cambio</th>
                      <th className="py-2 px-2 text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.length === 0 ? (
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td colSpan="4" className="py-2 px-2 text-slate-600 dark:text-slate-400">
                          Sin movimientos todavía.
                        </td>
                      </tr>
                    ) : (
                      ledger.map((m) => {
                        const delta = Number(m.delta) || 0;
                        return (
                          <tr key={m.id} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                              {new Date(m.created_at).toLocaleString('es-MX')}
                            </td>
                            <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                              {getReasonLabel(m.reason)}
                              {m.note ? <span className="text-slate-500 dark:text-slate-400"> · {m.note}</span> : null}
                            </td>
                            <td className={`py-2 px-2 text-right font-medium ${delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                              {delta >= 0 ? '+' : '−'}{formatCredits(Math.abs(delta))}
                            </td>
                            <td className="py-2 px-2 text-right text-slate-900 dark:text-slate-100">
                              {formatCredits(m.balance_after)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Usage History */}
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                Historial de consumo (detalle por llamada)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 px-2">Fecha</th>
                      <th className="py-2 px-2">Tipo</th>
                      <th className="py-2 px-2">Modelo</th>
                      <th className="py-2 px-2">Tokens (entrada/salida)</th>
                      <th className="py-2 px-2">Créditos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsage ? (
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td colSpan="5" className="py-2 px-2 text-slate-600 dark:text-slate-400">
                          Cargando…
                        </td>
                      </tr>
                    ) : usage.items.length === 0 ? (
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td colSpan="5" className="py-2 px-2 text-slate-600 dark:text-slate-400">
                          Sin consumo todavía.
                        </td>
                      </tr>
                    ) : (
                      usage.items.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-slate-100 dark:border-slate-800"
                        >
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                            {new Date(item.created_at).toLocaleString('es-MX')}
                          </td>
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                            {getKindLabel(item.kind)}
                          </td>
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                            {item.model || '—'}
                          </td>
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                            {(item.prompt_tokens ?? 0)} / {(item.completion_tokens ?? 0)}
                          </td>
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100">
                            {formatCredits(item.credits_charged)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  disabled={!canPrevious}
                >
                  Anterior
                </Button>
                {usage.total > 0 && (
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    Mostrando {displayStart}-{displayEnd} de {usage.total}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + LIMIT)}
                  disabled={!canNext}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
